import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { Loader2, ShoppingBag, ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Public read-only Plyx shop for a single creator.
 *   /plyx/u/:handle/shop
 *
 * Renders a 2-col grid of product cards. Each card deep-links to the
 * existing AfriMarket listing page so checkout flows through the
 * already-battle-tested Stripe pipeline.
 */
const PlyxShop = () => {
  const { handle } = useParams();
  const [creator, setCreator] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await plyxApi.get(`/plyx/u/${handle}/shop`);
        if (!alive) return;
        setCreator(data.creator);
        setProducts(data.products || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Shop not found");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [handle]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center" data-testid="plyx-shop-loading">
        <Loader2 className="w-6 h-6 animate-spin opacity-60"/>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-shop-page">
      <div className="max-w-3xl mx-auto px-5 pt-6 pb-24">
        <Link to={`/plyx/u/${handle}`} className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mb-4" data-testid="plyx-shop-back">
          <ArrowLeft className="w-4 h-4"/> back to @{handle}
        </Link>
        <div className="flex items-center gap-4 mb-6">
          {creator?.avatar_url ? (
            <img src={plyxMediaUrl(creator.avatar_url)} alt={creator.display_name} className="w-14 h-14 rounded-full object-cover ring-2 ring-[#FFB42D]"/>
          ) : (
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF2D7A] to-[#FFB42D] flex items-center justify-center font-bold text-2xl">
              {(creator?.display_name || creator?.handle || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <div className="font-display text-2xl tracking-tight">{creator?.display_name || creator?.handle}'s Shop</div>
            <div className="text-xs text-white/50 mt-0.5 inline-flex items-center gap-2">
              <ShoppingBag className="w-3 h-3 text-[#FFB42D]"/>
              {products.length} product{products.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {products.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center" data-testid="plyx-shop-empty">
            <ShoppingBag className="w-10 h-10 mx-auto text-white/30 mb-3"/>
            <div className="font-semibold">No products yet</div>
            <div className="text-xs text-white/50 mt-1">@{handle} hasn't added anything to their Plyx shop.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {products.map((p) => (
              <a
                key={p.listing_id}
                href={p.listing_url}
                target="_top"
                data-testid={`plyx-shop-product-${p.listing_id}`}
                className="group rounded-2xl overflow-hidden border border-white/10 bg-white/5 hover:border-[#FF2D7A] transition-all hover:scale-[1.02]"
              >
                <div className="aspect-square bg-black/50 overflow-hidden">
                  {p.thumbnail_url ? (
                    <img src={p.thumbnail_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform" loading="lazy"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20">
                      <ShoppingBag className="w-10 h-10"/>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-sm font-semibold line-clamp-2 leading-tight" title={p.title}>{p.title}</div>
                  <div className="mt-1.5 flex items-baseline justify-between">
                    <div className="font-display text-lg text-[#FFB42D] tabular-nums">
                      {p.currency === "GBP" ? "£" : p.currency === "USD" ? "$" : p.currency === "EUR" ? "€" : `${p.currency} `}
                      {p.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-white/40 group-hover:text-white/80"/>
                  </div>
                  {p.stock <= 0 && (
                    <div className="text-[10px] text-red-400 mt-1 uppercase tracking-wider">Sold out</div>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlyxShop;
